/**
 * Created by Daniel Wang on 1/29/2024.
 * Description: persistent cache component
 * ------ maintenance history ------
 */

import { ColDef, GridApi, GridOptions } from 'ag-grid-community';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { of, Subscription } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { AdminPagesService } from '../../../../tamalelibs/services/admin-page.service';
import { ToastService } from '../../../../widgets/toast/toast.service';
import { PurgeCacheType, PurgeTypeFeedback } from '../../../../tamalelibs/models/admin-page.model';
import { AlertWindowService } from '../../../../widgets/alert-window/alert-window.service';
import { UsersService } from '../../../../tamalelibs/services/users.service';
import { ArrayHelperService } from '../../../../tamalelibs/services/array-helper.service';
import { AdminPageLinkComponent } from '../../admin-page-actions/admin-page-link.component';
import { adminPagesConstants } from '../../admin-page-setting.constants';
import { AdminPageSetting } from '../../admin-page-setting.model';
import { NotificationStyles } from '../../../../widgets/notification/notification.model';
import { StringLiteralsPipe } from '../../../../pipes/translate.pipe';

@Component({
    selector: 'tam-persistent-cache',
    templateUrl: './persistent-cache.component.html',
    styleUrls: ['./persistent-cache.component.scss']
})
export class PersistentCacheComponent implements OnInit, OnDestroy {
    // #region public properties
    columnDefs: Array<ColDef>;
    gridOptions: GridOptions;

    noDataStyle = 'none';
    reloading = false;
    rowData: Array<any> = [];
    // #endregion

    // #region private properties
    private _destroySubscriptions: Array<Subscription> = [];
    private _gridApi: GridApi;
    private _persistentCachePage: AdminPageSetting;
    // #endregion

    // #region constructor
    constructor(
        private _adminPagesServcie: AdminPagesService,
        private _toast: ToastService,
        private _usersService: UsersService
    ) {
    }
    // #endregion

    // #region angular functions
    ngOnInit() {
        this._persistentCachePage = adminPagesConstants['persistent-cache'];
        this._destroySubscriptions.push(
            this._persistentCachePage.feedbackSubject$.subscribe(feedback => this._handleGridFeedback(feedback))
        );
        this._initAGGridProperties();
        this._initAGGridColumns();
        this._initAGGridData();
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
    }
    // #endregion

    // #region public functions
    onGridReady(params) {
        this._gridApi = params.api;
        this._gridApi.sizeColumnsToFit();
    }
    onClickPurgeAll(event, cacheType) {
        const data = {
            cacheType: cacheType
        };
        this._purgeCache(data, 'all users', cacheType);
    }
    // #endregion

    // #region private functions
    private _handleGridFeedback(feedback) {
        if (feedback.type === PurgeTypeFeedback.Purge) {
            const purgeCacheType = this._adminPagesServcie.findKeyByValue(PurgeCacheType, feedback.payload.purgeType);
            const data = {
                userID: feedback.payload.userID,
                cacheType: purgeCacheType
            };
            this._purgeCache(data, feedback.payload.userName, purgeCacheType);
        }
    }

    private _initAGGridColumns() {
        this.columnDefs = [{
            headerName: 'User',
            field: 'username',
            colId: 'username',
            menuTabs: [],
            comparator: (a, b) => {
                if (typeof a === 'string') {
                    return a.localeCompare(b);
                } else {
                    return (a > b ? 1 : (a < b ? -1 : 0));
                }
            },
        }, {
            headerName: '',
            cellRenderer: AdminPageLinkComponent,
            cellRendererParams(params) {
                const config = {
                    purgeType: PurgeCacheType.all,
                    feedbackSubject$: () => adminPagesConstants['persistent-cache'].feedbackSubject$
                };
                return config;
            },
            sortable: false,
            suppressMovable: true,
            cellStyle: { 'text-align': 'center' },
        }, {
            headerName: '',
            cellRenderer: AdminPageLinkComponent,
            cellRendererParams(params) {
                const config = {
                    purgeType: PurgeCacheType.files,
                    feedbackSubject$: () => adminPagesConstants['persistent-cache'].feedbackSubject$
                };
                return config;
            },
            sortable: false,
            suppressMovable: true,
            cellStyle: { 'text-align': 'center' },
        }, {
            headerName: '',
            cellRenderer: AdminPageLinkComponent,
            cellRendererParams(params) {
                const config = {
                    purgeType: PurgeCacheType.metadata,
                    feedbackSubject$: () => adminPagesConstants['persistent-cache'].feedbackSubject$
                };
                return config;
            },
            sortable: false,
            suppressMovable: true,
            cellStyle: { 'text-align': 'center' },
        }];
    }

    private _initAGGridData() {
        this.reloading = true;
        this._destroySubscriptions.push(
            this._usersService.getAdminPageUsersList().subscribe(res => {
                const userList = res['user-list'].map(user => ({ userID: user.id, username: user.username }));
                ArrayHelperService.sort(userList, 'username');
                this.rowData = userList;
                this.reloading = false;

            })
        );
    }

    private _initAGGridProperties() {
        this.gridOptions = {
            defaultColDef: {
                sortable: true,
                resizable: true,
                menuTabs: []
            },
            cacheBlockSize: 50,
            suppressCellFocus: true,
            suppressColumnVirtualisation: true,
            enableBrowserTooltips: true,
            rowSelection: 'single',
            sortingOrder: ['desc', 'asc'],
            maintainColumnOrder: true,
            suppressContextMenu: true,
            suppressDragLeaveHidesColumns: true,
            suppressRowGroupHidesColumns:true,
        };
    }
    private _purgeCache(data, username, purgeCacheType) {
        this._adminPagesServcie.persistentCachePurge(data).pipe(
            take(1),
            catchError(error => {
                this._toast.notify({
                    style: NotificationStyles.Success,
                    message: error
                });
                return of(error);
            })
        ).subscribe(res => {
            if (res && res.message.toUpperCase() === StringLiteralsPipe.translate('general.ok')) {
                let type = purgeCacheType;
                if (purgeCacheType === 'all') {
                    type = 'files and metadata';
                }
                const message = 'Persistent cache ' + type + ' for ' + username + ' purged.';

                this._toast.notify({
                    style: NotificationStyles.Success,
                    message: message
                });
            }
        });
    }
    // #endregion
}
